/**
 * FeedbackLoop component - Daily guidance feedback with micro-reward.
 * Shows 3 options (Yes/Partially/Not today), displays micro-reward on submission,
 * and locks after response is recorded.
 */

import React from 'react';
import { useSubmitGuidanceFeedback } from '../../services/api';
import { FeedbackResponse, GuidanceFeedback } from '../../types';

interface FeedbackLoopProps {
  checkinId: string;
  existingFeedback?: GuidanceFeedback | null;
}

const FEEDBACK_OPTIONS: { value: FeedbackResponse; label: string; icon: string }[] = [
  { value: 'followed', label: 'Yes', icon: '✅' },
  { value: 'partially', label: 'Partially', icon: '〰️' },
  { value: 'not_today', label: 'Not today', icon: '⏭️' },
];

const FeedbackLoop: React.FC<FeedbackLoopProps> = ({ checkinId, existingFeedback }) => {
  const submitFeedback = useSubmitGuidanceFeedback();

  const handleSubmit = (response: FeedbackResponse) => {
    if (existingFeedback || submitFeedback.isPending) return;
    submitFeedback.mutate({ checkinId, response });
  };

  // Show locked state with micro-reward if feedback already submitted
  const feedback = existingFeedback || (submitFeedback.isSuccess ? submitFeedback.data?.guidance_feedback : null);

  if (feedback) {
    return (
      <div className="rounded-xl p-5 bg-gradient-to-br from-green-50 to-white border border-green-200">
        <div className="flex items-start space-x-3 mb-3">
          <span className="text-2xl flex-shrink-0">🌟</span>
          <div>
            <p className="text-sm font-semibold text-green-800 mb-1">
              {feedback.response === 'followed' && 'You followed your guidance today'}
              {feedback.response === 'partially' && 'You partially followed your guidance'}
              {feedback.response === 'not_today' && 'That\'s okay — tomorrow is a fresh signal'}
            </p>
            <p className="text-green-700 text-sm leading-relaxed italic">
              {feedback.micro_reward}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl p-5 bg-gradient-to-br from-neutral-50 to-white border border-neutral-200">
      <p className="text-sm font-semibold text-neutral-800 mb-3">
        Did you follow today's guidance?
      </p>
      <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
        {FEEDBACK_OPTIONS.map(({ value, label, icon }) => (
          <button
            key={value}
            onClick={() => handleSubmit(value)}
            disabled={submitFeedback.isPending}
            className="flex-1 flex items-center justify-center space-x-2 px-4 py-3 min-h-[44px] rounded-lg border-2 border-neutral-200 bg-white hover:border-primary-400 hover:bg-primary-50 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span>{icon}</span>
            <span className="text-sm font-medium text-neutral-700">{label}</span>
          </button>
        ))}
      </div>
      {submitFeedback.isError && (
        <p className="text-xs text-red-600 mt-2">
          Failed to submit feedback. Please try again.
        </p>
      )}
    </div>
  );
};

export default FeedbackLoop;
